import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  ServiceFacilityDTO,
} from 'dtos';
import {
  useUpsertServiceFacility,
} from 'views/EMR/Settings/hooks';
import {
  getSubmitButtonText,
  isEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import AddressFields, {
  AddressForm,
} from 'pages/Dashboard/components/AddressFields';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import Divider from '@mui/material/Divider';
import {
  validateAddressWithLongZipCode,
} from 'utils/react-hook-form';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Tooltip from 'core/components/Tooltip';
import NPI from 'views/EMR/Settings/BillingConfigurations/NPI';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import MaskedTextField from 'core/components/MaskedTextField';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';

export type ServiceFacilityDialogProps = {
  facility?: ServiceFacilityDTO | null;
};

type Props = ServiceFacilityDialogProps & {
  onClose: () => void;
};

type FormValues = ServiceFacilityDTO;

const defaultValues: FormValues = {
  nickname: '',
  officialName: '',
  isInternal: false,
  isPrincipal: false,
  isActive: true,
  isInboundSchedulingEnabled: false,
  defaultPOS: '',
  npi: undefined,
};

const posMask = [{
  mask: '00',
  lazy: false,
  overwrite: true,
}];

export const isValidClia = (value: string) => isEmptyString(value) || /^\d{2}D\d{7}$/.test(value.trim());

export default function ServiceFacilityDialog({
  onClose,
  facility,
}: Props) {
  const isNew = isNil(facility);

  const initialFormValues: FormValues = {
    ...(facility ?? defaultValues),
  };

  const formProps = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: initialFormValues,
  });

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isValid, isSubmitting },
  } = formProps;

  const [isInternal, isActive] = watch(['isInternal', 'isActive']);

  const { mutateAsync: upsertServiceFacility } = useUpsertServiceFacility();
  const notify = useNotify();
  const userInfo = useUserInfo();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    const {
      ...serviceFacilityDtoData
    } = data;

    try {
      await upsertServiceFacility({
        ...serviceFacilityDtoData,
        organizationId: userInfo?.organization?.organizationId,
      });

      notify({
        variant: 'success',
        message: `Service Facility ${isNew ? 'created' : 'updated'} successfully`,
      });

      settingsEventChannel.emit('onServiceFacilityChange');
      handleClose();
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title={(
          <div className="flex flex-1 flex-col gap-4">
            <h2 className="text-lg font-semibold">
              {`${isNew ? 'Add' : 'Edit'} Service Facility`}
            </h2>
          </div>
        )}
        closeBtnClasses="!-top-1"
        onClose={onClose}
        onConfirm={noop}
        className="!w-176 populate-form"
        maxWidth="lg"
        open
      >
        <div className="flex flex-col gap-form-row">
          <div className="flex flex-col gap-form-row">
            <div className="flex gap-form-field">
              <Controller
                name="nickname"
                control={control}
                rules={{ required: 'Nickname is required' }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    id="nickname-name"
                    label="Nickname"
                    className="flex-1"
                    inputProps={{ maxLength: 64 }}
                    required
                  />
                )}
              />
              <Controller
                name="officialName"
                control={control}
                rules={{ required: 'Official Name is required' }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    className="flex-1"
                    id="official-name"
                    label="Official Name"
                    inputProps={{ maxLength: 64 }}
                    required
                  />
                )}
              />
              <Controller
                name="defaultPOS"
                control={control}
                rules={{
                  required: 'POS is required',
                  validate: (value) => (value as string)?.length === 2 || 'POS should contain exactly 2 characters',
                }}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                  <Wrapper
                    id="pos"
                    label="POS"
                    className="w-10"
                    showErrorText={false}
                    error={invalid}
                    required
                  >
                    <MaskedTextField
                      {...field}
                      unmask
                      mask={posMask}
                      value={value}
                      placeholder="POS"
                      onAccept={onChange}
                    />
                  </Wrapper>
                )}
              />
            </div>
            <div className="flex flex-col">
              <Controller
                name="address"
                control={control}
                rules={{
                  required: 'Address is required',
                  validate: (value) => (validateAddressWithLongZipCode(value) || 'Enter valid address'),
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <AddressFields
                    {...field}
                    value={field.value as AddressForm ?? {}}
                    isLongZipRequired
                    required
                    error={invalid}
                  />
                )}
              />
            </div>
            <div className="flex flex-col">
              <Controller
                name="clia"
                control={control}
                rules={{
                  validate: (value) => isValidClia(value ?? '') || 'Invalid CLIA. Expected ##D####### (e.g. 12D3456789).',
                }}
                render={({ field, fieldState: { invalid } }) => (
                  <Input
                    {...field}
                    error={invalid}
                    showErrorText={false}
                    className="flex-1"
                    id="clia"
                    label="CLIA"
                    inputProps={{ maxLength: 10 }}
                  />
                )}
              />
            </div>
            <div className="flex gap-form-field">
              <Controller
                name="isActive"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <Checkbox
                    {...field}
                    id="is-active-toggle"
                    checked={value !== false}
                    value={(value !== false).toString()}
                    onChange={(checked: boolean) => {
                      onChange(checked);
                      if (!checked) {
                        setValue('isPrincipal', false, { shouldValidate: true });
                      }
                    }}
                    label="Active"
                    disabled={facility?.isPrincipal}
                  />
                )}
              />
              <Divider orientation="vertical" flexItem />
              <Controller
                name="isPrincipal"
                control={control}
                render={({ field: { value, ...field } }) => {
                  const isPrincipal = Boolean(value);
                  return (
                    <Tooltip
                      hide={!facility?.isPrincipal}
                      title={(
                        <>
                          Only one principal is allowed.
                          <br />
                          Setting a new one will unset the current.
                        </>
                        )}
                    >
                      <span>
                        <Checkbox
                          {...field}
                          id="is-principal-toggle"
                          checked={isPrincipal}
                          value={(value ?? '').toString()}
                          label="Principal"
                          disabled={facility?.isPrincipal || isActive === false}
                        />
                      </span>
                    </Tooltip>
                  );
                }}
              />
              <Divider orientation="vertical" flexItem />
              <Controller
                name="isInternal"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="is-internal-toggle"
                    checked={Boolean(field.value)}
                    value={(field?.value ?? '').toString()}
                    label="Internal"
                  />
                )}
              />
              <Divider orientation="vertical" flexItem />
              <Controller
                name="isInboundSchedulingEnabled"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    id="is-inbound-scheduling-enabled-toggle"
                    checked={Boolean(field.value)}
                    value={(field?.value ?? '').toString()}
                    label="Inbound Scheduling"
                  />
                )}
              />
            </div>
            {!isInternal && (
              <NPI />
            )}
          </div>
          <div className="flex flex-row-reverse gap-2 justify-between">
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(isNew, isSubmitting)}
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
          </div>
        </div>
      </Dialog>
    </FormProvider>
  );
}
